import React from 'react';
import { Plus, MessageSquare, LayoutDashboard, Zap, Repeat, GraduationCap, Ticket, Sparkles, ChevronsUpDown, Clock, Folder, ChevronRight } from 'lucide-react';
import { Button } from './Button';
import { ScrollArea } from './ScrollArea';
import { Avatar, AvatarFallback } from './Avatar';
interface SidebarProps {
  activeChatId: string | null;
  isChatPanelOpen: boolean;
  isProjectsPanelOpen: boolean;
  onToggleChatPanel: () => void;
  onToggleProjectsPanel: () => void;
  onNewChat: () => void;
}
export function Sidebar({
  activeChatId,
  isChatPanelOpen,
  isProjectsPanelOpen,
  onToggleChatPanel,
  onToggleProjectsPanel,
  onNewChat
}: SidebarProps) {
  return <div className="w-[280px] h-full bg-[#1c2033] border-r border-white/5 flex flex-col text-sm text-slate-300 flex-shrink-0 relative z-20" data-id="element-131">
      {/* Header / Logo */}
      <div className="p-4 flex items-center justify-between" data-id="element-132">
        <div className="flex items-center gap-2 font-bold text-white tracking-wide" data-id="element-133">
          <div className="w-6 h-6 bg-red-600 rounded flex items-center justify-center text-[10px]" data-id="element-134">
            M
          </div>
          MARKETING SECRETS
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-white" data-id="element-135">
          <div className="h-4 w-4" data-id="element-136" />
        </Button>
      </div>

      {/* Team Selector */}
      <div className="px-4 pb-4" data-id="element-137">
        <Button variant="ghost" className="w-full justify-between px-2 h-8 text-slate-300 hover:text-white hover:bg-white/5" data-id="element-138">
          <div className="flex items-center gap-2" data-id="element-139">
            <div className="w-4 h-4 rounded bg-[#2a2f42] flex items-center justify-center text-[10px]" data-id="element-140">
              t
            </div>
            <span data-id="element-141">test</span>
          </div>
          <ChevronsUpDown className="h-3 w-3 opacity-50" data-id="element-142" />
        </Button>
      </div>

      <ScrollArea className="flex-1 px-2" data-id="element-143">
        {/* Chief of Staff Section */}
        <div className="mb-6" data-id="element-144">
          <div className="px-2 mb-2 text-xs font-semibold text-[#6b7194] tracking-wider" data-id="element-145">
            CHIEF OF STAFF
          </div>

          <div className="space-y-0.5" data-id="element-146">
            <div className="px-2" data-id="element-147">
              <Button variant="ghost" className="w-full justify-between h-8 px-2 bg-[#252a42] text-slate-200 hover:bg-[#2d3250] font-normal" data-id="element-148">
                <div className="flex items-center" data-id="element-149">
                  <div className="w-5 h-5 rounded-full bg-[#6c5ce7] flex items-center justify-center text-[9px] font-bold text-white mr-2" data-id="element-150">
                    CS
                  </div>
                  Your Chief of Staff
                </div>
                <div className="flex items-center gap-1.5" data-id="element-151">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-152" />
                  <span className="text-[10px] text-emerald-500 font-medium" data-id="element-153">
                    Active
                  </span>
                  <ChevronRight className="h-3 w-3 text-slate-500 ml-1" data-id="element-154" />
                </div>
              </Button>
            </div>

            <div className="px-2" data-id="element-155">
              <Button onClick={onNewChat} variant="ghost" className="w-full justify-start h-8 px-2 gap-2 text-[#8b8fa8] hover:text-slate-200 hover:bg-white/5 font-normal" data-id="element-156">
                <MessageSquare className="h-4 w-4 opacity-70" data-id="element-157" />
                New Chat
              </Button>
            </div>

            <div className="px-2" data-id="element-158">
              <Button variant="ghost" onClick={onToggleChatPanel} className={`w-full justify-start h-8 px-2 font-normal ${isChatPanelOpen ? 'bg-[#252a42] text-white' : 'text-[#8b8fa8] hover:text-slate-200 hover:bg-white/5'}`} data-id="element-159">
                <Clock className="h-4 w-4 mr-2 opacity-70" data-id="element-160" />
                History
              </Button>
            </div>

            <div className="px-2 group/proj" data-id="element-161">
              <div className="flex items-center" data-id="element-162">
                <Button variant="ghost" onClick={onToggleProjectsPanel} className={`flex-1 justify-start h-8 px-2 font-normal ${isProjectsPanelOpen ? 'bg-[#252a42] text-white' : 'text-[#8b8fa8] hover:text-slate-200 hover:bg-white/5'}`} data-id="element-163">
                  <Folder className="h-4 w-4 mr-2 opacity-70" data-id="element-164" />
                  Projects
                </Button>
                <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover/proj:opacity-100 text-[#6b7194] hover:text-white hover:bg-white/10 flex-shrink-0" data-id="element-165">
                  <Plus className="h-3.5 w-3.5" data-id="element-166" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Workspace Section */}
        <div className="mb-6" data-id="element-167">
          <div className="px-2 mb-2 text-xs font-semibold text-[#6b7194] tracking-wider" data-id="element-168">
            WORKSPACE
          </div>
          <div className="space-y-0.5" data-id="element-169">
            <NavItem icon={<LayoutDashboard className="h-4 w-4" data-id="element-171" />} label="Dashboard" data-id="element-170" />
            <NavItem icon={<Sparkles className="h-4 w-4" data-id="element-173" />} label="Attractive Characters" data-id="element-172" />
            <NavItem icon={<Zap className="h-4 w-4" data-id="element-175" />} label="Sprints" data-id="element-174" />
            <NavItem icon={<Repeat className="h-4 w-4" data-id="element-177" />} label="Routines" data-id="element-176" />
            <NavItem icon={<GraduationCap className="h-4 w-4" data-id="element-179" />} label="Training & Courses" data-id="element-178" />
            <NavItem icon={<Ticket className="h-4 w-4" data-id="element-181" />} label="Backstage Pass" data-id="element-180" />
            <NavItem icon={<div className="h-4 w-4" data-id="element-183" />} label="Software" data-id="element-182" />
          </div>
        </div>
      </ScrollArea>

      {/* User Profile */}
      <div className="p-3 border-t border-white/5" data-id="element-184">
        <Button variant="ghost" className="w-full justify-start px-2 h-12 hover:bg-white/5" data-id="element-185">
          <Avatar className="h-8 w-8 mr-3 rounded-md bg-[#252a42]" data-id="element-186">
            <AvatarFallback className="bg-[#252a42] text-slate-300 rounded-md" data-id="element-187">
              J
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-start text-left flex-1 overflow-hidden" data-id="element-188">
            <span className="text-sm font-medium text-slate-200 truncate w-full" data-id="element-189">
              jase
            </span>
            <span className="text-xs text-[#6b7194] truncate w-full" data-id="element-190">
              jase@clickfunnels.com
            </span>
          </div>
          <ChevronsUpDown className="h-4 w-4 text-[#6b7194]" data-id="element-191" />
        </Button>
      </div>
    </div>;
}
function NavItem({
  icon,
  label
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return <div className="px-2" data-id="element-192">
      <Button variant="ghost" className="w-full justify-start h-8 px-2 text-[#8b8fa8] hover:text-slate-200 hover:bg-white/5 font-normal" data-id="element-193">
        <span className="mr-2 opacity-70" data-id="element-194">{icon}</span>
        {label}
      </Button>
    </div>;
}